import React, { useState } from 'react';
import { responseHero } from '../../../utils/axiosInterceptor';
import { toast } from 'react-toastify';
import { formatCrypto } from '../../../utils/formatCrypto';

interface RobotToFixModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    btcAmount: number;
}

export const RobotToFixModal: React.FC<RobotToFixModalProps> = ({ isOpen, onClose, onSuccess, btcAmount }) => {
    const [note, setNote] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Trigger the backend migration service via our express routing boundary
            await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/wallet/transfer-robot-to-fix`, {
                note: note.trim() || undefined
            });

            toast.success("Sikeres állomány átcsoportosítás!");
            setNote('');
            onSuccess();
        } catch (error: any) {
            console.error("Hiba az átmozgatás során:", error);
            toast.error(error?.response?.data?.message || "Hiba történt a Fix zsebbe való átcsoportosítás során.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-blue-50/30">
                    <div>
                        <h3 className="text-lg font-black text-slate-800 tracking-tight">Biztonsági Átcsoportosítás</h3>
                        <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Robot BTC → Fix Tárca Migráció</p>
                    </div>
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="text-slate-400 hover:text-slate-600 text-2xl transition-colors leading-none"
                        disabled={submitting}
                    >
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 text-center">
                        <p className="text-xs font-bold text-slate-500 mb-1">
                            Biztosan átmozgatod az eddig termelt
                        </p>
                        <div className="text-2xl font-black text-slate-900 tracking-tight font-mono my-1">
                            {formatCrypto(btcAmount)} <span className="text-sm font-normal text-slate-400">BTC</span>
                        </div>
                        <p className="text-xs font-bold text-slate-500">
                            mennyiséget a fix zsebbe?
                        </p>
                        <span className="inline-block mt-3 text-[9px] font-black uppercase px-2 py-0.5 rounded bg-blue-100 text-blue-700 tracking-wider">
                            Minden Befektetői Csoport Arányosan Frissül
                        </span>
                    </div>

                    <div>
                        <label className="block text-[9px] font-black uppercase text-slate-400 mb-1.5 tracking-wider">
                            Megjegyzés (Opcionális)
                        </label>
                        <textarea
                            className="w-full bg-white border border-slate-200 focus:border-blue-400 rounded-xl px-3 py-2 text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/10 transition-all resize-none min-h-[80px]"
                            placeholder="Pl.: Havi robot hozam lefixálása..."
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={submitting}
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-1/3 py-3 rounded-xl font-bold text-xs uppercase text-slate-500 hover:bg-slate-50 border border-slate-100 transition-all"
                            disabled={submitting}
                        >
                            Mégse
                        </button>
                        <button
                            type="submit"
                            className="w-2/3 py-3 rounded-xl font-black text-xs uppercase tracking-wider text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-all shadow-md shadow-blue-600/10 active:scale-98"
                            disabled={submitting || btcAmount <= 0}
                        >
                            {submitting ? 'Feldolgozás...' : 'Átmozgatás Jóváhagyása'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};